import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChartDataPoint, PuttingResult } from "@/types/puttingResults";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend, BarChart, Bar } from "recharts";
import { format } from "date-fns";
import { enUS, fr, es } from "date-fns/locale";
import ExerciseProgressionTabs from "./ExerciseProgressionTabs";
import { SwingTendency } from "@/components/putting/training/charts/swing/types";
import { translateExerciseName } from "@/utils/exerciseTranslations";
import { getTestColor } from "@/components/putting/training/charts/TestColorConfig";

interface TrainingResultsChartProps {
  chartDataByTest: Record<string, ChartDataPoint[]>;
  testTypes: string[];
  selectedLevel: string | null;
  trainingResults: PuttingResult[];
  swingTendencies?: SwingTendency[];
  traineeUserId?: string;
}

const TrainingResultsChart: React.FC<TrainingResultsChartProps> = ({
  chartDataByTest,
  testTypes,
  selectedLevel,
  trainingResults,
  swingTendencies = [],
  traineeUserId
}) => {
  const { t, i18n } = useTranslation('common');
  const [viewMode, setViewMode] = useState<"line" | "bar" | "exercises">("exercises");

  // Get locale for date formatting
  const getLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };

  const filteredData = selectedLevel 
    ? trainingResults.filter(r => String(r.test_level) === selectedLevel)
    : trainingResults;

  const prepareProgressData = () => {
    const progressByTest: Record<string, any[]> = {};
    
    filteredData.forEach(result => {
      const testName = result.test_name || result.test_id || 'Standard';
      if (!progressByTest[testName]) {
        progressByTest[testName] = [];
      }
      
      progressByTest[testName].push({
        date: format(new Date(result.test_date), i18n.language === 'en' ? 'MM/dd/yy' : 'dd/MM/yy', { locale: getLocale() }),
        points: result.points,
        level: result.test_level,
        fullDate: result.test_date
      });
    });

    Object.keys(progressByTest).forEach(testName => {
      progressByTest[testName].sort((a, b) => 
        new Date(a.fullDate).getTime() - new Date(b.fullDate).getTime()
      );
    });

    return progressByTest;
  };

  const prepareStatsData = () => {
    const statsByTest: Record<string, { testName: string; avgPoints: number; totalTests: number; maxPoints: number }> = {};
    
    filteredData.forEach(result => {
      const testName = result.test_name || result.test_id || 'Standard';
      if (!statsByTest[testName]) {
        statsByTest[testName] = {
          testName,
          avgPoints: 0,
          totalTests: 0,
          maxPoints: 0
        };
      }
      
      statsByTest[testName].totalTests++;
      statsByTest[testName].avgPoints += result.points;
      statsByTest[testName].maxPoints = Math.max(statsByTest[testName].maxPoints, result.points);
    });

    return Object.values(statsByTest).map(stat => ({
      ...stat,
      testName: translateExerciseName(stat.testName, t),
      avgPoints: Math.round((stat.avgPoints / stat.totalTests) * 10) / 10
    }));
  };

  const progressData = prepareProgressData();
  const statsData = prepareStatsData();

  // Fallback colors array if getTestColor doesn't find a match
  const colors = [
    "#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", 
    "#06b6d4", "#84cc16", "#f97316", "#ec4899", "#6366f1"
  ];

  if (filteredData.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-500">
          <p>{t('puttingTraining.charts.analysis.noResults')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>{t('puttingTraining.charts.analysis.title')}</CardTitle>
            <div className="flex gap-2">
              <Button
                variant={viewMode === "exercises" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("exercises")}
              >
                {t('puttingTraining.charts.analysis.buttons.exercises')}
              </Button>
              <Button
                variant={viewMode === "line" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("line")}
              >
                {t('puttingTraining.charts.analysis.buttons.progression')}
              </Button>
              <Button
                variant={viewMode === "bar" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("bar")}
              >
                {t('puttingTraining.charts.analysis.buttons.statistics')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === "exercises" ? (
            <ExerciseProgressionTabs 
              trainingResults={trainingResults}
              selectedLevel={selectedLevel}
              swingTendencies={swingTendencies}
              traineeUserId={traineeUserId}
            />
          ) : viewMode === "line" ? (
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickMargin={8}
                  />
                  <YAxis 
                    domain={[0, 15]}
                    tick={{ fontSize: 12 }}
                    tickMargin={8}
                    label={{ value: t('puttingTraining.charts.axes.points'), angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value} pts`, name]}
                  />
                  <Legend />
                  
                  {Object.keys(progressData).slice(0, 5).map((testName, index) => {
                    const color = getTestColor(testName) || colors[index % colors.length];
                    console.log(`TrainingResultsChart - Test: ${testName}, Color: ${color}`);
                    return (
                      <Line
                        key={testName}
                        data={progressData[testName]}
                        type="monotone"
                        dataKey="points"
                        name={translateExerciseName(testName, t)}
                        stroke={color}
                        strokeWidth={3}
                        dot={{ strokeWidth: 2, r: 5, fill: color }}
                        activeDot={{ strokeWidth: 0, r: 7, fill: color }}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statsData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="testName" 
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    label={{ value: t('puttingTraining.charts.axes.points'), angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === "avgPoints") return [`${value} pts`, t('puttingTraining.charts.legend.average')];
                      if (name === "maxPoints") return [`${value} pts`, t('puttingTraining.charts.legend.maximum')];
                      if (name === "totalTests") return [`${value}`, t('puttingTraining.charts.legend.numberOfTests')];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="avgPoints" fill="#10b981" name={t('puttingTraining.charts.legend.average')} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="maxPoints" fill="#3b82f6" name={t('puttingTraining.charts.legend.maximum')} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedLevel && (
        <Card>
          <CardHeader>
            <CardTitle>{t('puttingTraining.charts.summary.title')} {selectedLevel}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-golf-600">
                  {filteredData.length}
                </div>
                <p className="text-sm text-gray-500">{t('puttingTraining.charts.summary.testsCompleted')}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-golf-600">
                  {Math.round((filteredData.reduce((sum, r) => sum + r.points, 0) / filteredData.length) * 10) / 10}
                </div>
                <p className="text-sm text-gray-500">{t('puttingTraining.charts.summary.averagePoints')}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-golf-600">
                  {Math.max(...filteredData.map(r => r.points))}
                </div>
                <p className="text-sm text-gray-500">{t('puttingTraining.charts.summary.bestScore')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TrainingResultsChart;
